/**
 * Authentication API Service
 * Interface với FastAPI /auth endpoints
 */

import { apiClient, ApiResponse } from './apiClient';

export interface RegisterRequest {
  email: string;
  password: string;
  full_name?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface GoogleLoginRequest {
  credential: string;
  clientId: string;
  role?: string;
  source_domain?: string;
}

export interface User {
  id: number;
  email: string;
  full_name?: string;
  avatar_url?: string;
  role: 'user' | 'teacher' | 'admin';
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  user: User;
}

class AuthApi {
  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<ApiResponse<User>> {
    return apiClient.post<User>('/auth/register', data, false);
  }

  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<ApiResponse<TokenResponse>> {
    const response = await apiClient.post<TokenResponse>('/auth/login', data, false);

    // Save token if login successful
    if (response.data?.access_token) {
      apiClient.setToken(response.data.access_token);
    }

    return response;
  }

  /**
   * Login with Google OAuth
   */
  async googleLogin(data: GoogleLoginRequest): Promise<ApiResponse<TokenResponse>> {
    const response = await apiClient.post<TokenResponse>('/auth/google', data, false);

    // Save token if login successful
    if (response.data?.access_token) {
      apiClient.setToken(response.data.access_token);
    }

    return response;
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiClient.get<User>('/users/me');
  }

  /**
   * Update user profile
   */
  async updateProfile(data: { full_name?: string; avatar_url?: string }): Promise<ApiResponse<User>> {
    return apiClient.put<User>('/users/update', data);
  }

  /**
   * Logout (clear token)
   */
  logout(): void {
    apiClient.clearToken();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('jwt_token');
  }
}

export const authApi = new AuthApi();

